"use client";

import React, { useCallback, useId, useMemo, useState } from "react";

import {
  emptyGuidedRequestQuoteAnswers,
  formatGuidedRequestQuoteMessage,
  guidedRequestQuoteBudgetOptions,
  guidedRequestQuoteServiceOptions,
  guidedRequestQuoteTimingOptions,
  isGuidedRequestQuoteValid,
  sanitizeGuidedRequestQuoteAnswers,
  validateGuidedRequestQuoteAnswers,
  type GuidedRequestQuoteAnswers,
  type GuidedRequestQuoteLabels,
} from "./guidedRequestQuote";

export type GuidedRequestQuoteComposerSubmitResult = Readonly<{
  threadId?: string;
}>;

export type GuidedRequestQuoteProjectLocationFieldProps = Readonly<{
  id: string;
  value: string;
  ariaDescribedBy?: string;
  ariaInvalid: boolean;
  onChange: (value: string) => void;
  placeholder: string;
}>;

export type GuidedRequestQuoteComposerProps = Readonly<{
  labels: GuidedRequestQuoteLabels;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (input: Readonly<{ message: string }>) => Promise<GuidedRequestQuoteComposerSubmitResult>;
  onOpenConversation?: (threadId: string) => void;
  /** Optional anonymous email/captcha slot rendered above actions on the form step. */
  identitySlot?: React.ReactNode;
  className?: string;
  instanceName?: string;
  /** Optional governed location field adapter, for example a backend catalog combobox. */
  renderProjectLocationField?: (
    props: GuidedRequestQuoteProjectLocationFieldProps,
  ) => React.ReactNode;
  /** Optional extra validation layered on top of the shared required-field contract. */
  validateProjectLocation?: (value: string) => string | undefined;
}>;

type ComposerStep = "form" | "review" | "success";

function classifySubmitError(
  error: unknown,
  labels: GuidedRequestQuoteLabels,
): string {
  const text = error instanceof Error ? error.message : String(error ?? "");
  const lower = text.toLowerCase();
  if (lower.includes("rate") || lower.includes("limited") || lower.includes("temporarily")) {
    return labels.rateLimited;
  }
  if (lower.includes("already submitted") || lower.includes("duplicate") || lower.includes("conflict")) {
    return labels.duplicate;
  }
  return labels.submitFailed;
}

export function GuidedRequestQuoteComposer({
  labels,
  isSubmitting,
  onCancel,
  onSubmit,
  onOpenConversation,
  identitySlot,
  className,
  instanceName = "guidedRequestQuoteComposer",
  renderProjectLocationField,
  validateProjectLocation,
}: GuidedRequestQuoteComposerProps) {
  const titleId = useId();
  const fieldId = useId();
  const [answers, setAnswers] = useState<GuidedRequestQuoteAnswers>(emptyGuidedRequestQuoteAnswers);
  const [step, setStep] = useState<ComposerStep>("form");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | undefined>(undefined);

  const previewMessage = useMemo(
    () => formatGuidedRequestQuoteMessage(answers, labels),
    [answers, labels],
  );

  const updateField = useCallback(
    <K extends keyof GuidedRequestQuoteAnswers>(key: K, value: GuidedRequestQuoteAnswers[K]) => {
      setAnswers((current) => ({ ...current, [key]: value }));
      setFieldErrors((current) => {
        if (!(key in current) && !("message" in current)) return current;
        const next = { ...current };
        delete next[key];
        delete next.message;
        return next;
      });
    },
    [],
  );

  const validateAnswers = useCallback((candidate: GuidedRequestQuoteAnswers) => {
    const errors = { ...validateGuidedRequestQuoteAnswers(candidate, labels) };
    const locationError = validateProjectLocation?.(
      sanitizeGuidedRequestQuoteAnswers(candidate).projectLocation,
    );
    if (locationError) {
      errors.projectLocation = locationError;
    }
    return errors;
  }, [labels, validateProjectLocation]);

  const goToReview = useCallback(() => {
    const errors = validateAnswers(answers);
    setFieldErrors(errors);
    setSubmitError(null);
    if (Object.keys(errors).length > 0) return;
    setAnswers(sanitizeGuidedRequestQuoteAnswers(answers));
    setStep("review");
  }, [answers, validateAnswers]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    const errors = validateAnswers(answers);
    if (!isGuidedRequestQuoteValid(answers, labels) || Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setStep("form");
      return;
    }
    setSubmitError(null);
    const message = formatGuidedRequestQuoteMessage(answers, labels);
    try {
      const result = await onSubmit({ message });
      setThreadId(result.threadId);
      setStep("success");
    } catch (error) {
      setSubmitError(classifySubmitError(error, labels));
    }
  }, [answers, isSubmitting, labels, onSubmit, validateAnswers]);

  return (
    <section
      className={["guided-request-quote", className].filter(Boolean).join(" ")}
      data-component-instance={instanceName}
      aria-labelledby={titleId}
    >
      {step === "success" ? (
        <div className="guided-request-quote__success" role="status">
          <h2 id={titleId}>{labels.successTitle}</h2>
          <p>{labels.successBody}</p>
          <div className="guided-request-quote__actions">
            {threadId && onOpenConversation ? (
              <button
                type="button"
                className="guided-request-quote__primary"
                onClick={() => onOpenConversation(threadId)}
              >
                {labels.openConversation}
              </button>
            ) : null}
            <button type="button" onClick={onCancel}>
              {labels.close}
            </button>
          </div>
        </div>
      ) : null}

      {step === "review" ? (
        <>
          <header className="guided-request-quote__header">
            <h2 id={titleId}>{labels.reviewHeading}</h2>
          </header>
          <label className="guided-request-quote__field">
            <span>{labels.messagePreviewLabel}</span>
            <textarea
              readOnly
              rows={10}
              value={previewMessage}
              className="guided-request-quote__preview"
            />
          </label>
          {submitError ? (
            <p className="guided-request-quote__error" role="alert">{submitError}</p>
          ) : null}
          <footer className="guided-request-quote__actions">
            <button type="button" onClick={() => setStep("form")} disabled={isSubmitting}>
              {labels.backToForm}
            </button>
            <button
              type="button"
              className="guided-request-quote__primary"
              onClick={() => { void handleSubmit(); }}
              disabled={isSubmitting}
            >
              {isSubmitting ? labels.submitting : labels.submit}
            </button>
          </footer>
        </>
      ) : null}

      {step === "form" ? (
        <>
          <header className="guided-request-quote__header">
            <h2 id={titleId}>{labels.heading}</h2>
          </header>

          <div className="guided-request-quote__field">
            <label htmlFor={`${fieldId}-service`}>{labels.serviceNeededLabel}</label>
            <select
              id={`${fieldId}-service`}
              value={answers.serviceNeeded}
              aria-invalid={Boolean(fieldErrors.serviceNeeded)}
              aria-describedby={fieldErrors.serviceNeeded ? `${fieldId}-service-error` : undefined}
              onChange={(event) => updateField(
                "serviceNeeded",
                event.target.value as GuidedRequestQuoteAnswers["serviceNeeded"],
              )}
            >
              <option value="">{labels.serviceNeededPlaceholder}</option>
              {guidedRequestQuoteServiceOptions.map((option) => (
                <option key={option} value={option}>
                  {labels.serviceOptions[option]}
                </option>
              ))}
            </select>
            {fieldErrors.serviceNeeded ? (
              <span id={`${fieldId}-service-error`} className="guided-request-quote__field-error">
                {fieldErrors.serviceNeeded}
              </span>
            ) : null}
          </div>

          <div className="guided-request-quote__field">
            <label htmlFor={`${fieldId}-location`}>{labels.projectLocationLabel}</label>
            {renderProjectLocationField ? renderProjectLocationField({
              id: `${fieldId}-location`,
              value: answers.projectLocation,
              ariaInvalid: Boolean(fieldErrors.projectLocation),
              ariaDescribedBy: fieldErrors.projectLocation
                ? `${fieldId}-location-error`
                : undefined,
              onChange: (value) => updateField("projectLocation", value),
              placeholder: labels.projectLocationPlaceholder,
            }) : (
              <input
                id={`${fieldId}-location`}
                type="text"
                value={answers.projectLocation}
                aria-invalid={Boolean(fieldErrors.projectLocation)}
                aria-describedby={fieldErrors.projectLocation ? `${fieldId}-location-error` : undefined}
                onChange={(event) => updateField("projectLocation", event.target.value)}
                placeholder={labels.projectLocationPlaceholder}
                maxLength={200}
                autoComplete="off"
              />
            )}
            {fieldErrors.projectLocation ? (
              <span id={`${fieldId}-location-error`} className="guided-request-quote__field-error">
                {fieldErrors.projectLocation}
              </span>
            ) : null}
          </div>

          <div className="guided-request-quote__field">
            <label htmlFor={`${fieldId}-timing`}>{labels.preferredTimingLabel}</label>
            <select
              id={`${fieldId}-timing`}
              value={answers.preferredTiming}
              aria-invalid={Boolean(fieldErrors.preferredTiming)}
              aria-describedby={fieldErrors.preferredTiming ? `${fieldId}-timing-error` : undefined}
              onChange={(event) => updateField(
                "preferredTiming",
                event.target.value as GuidedRequestQuoteAnswers["preferredTiming"],
              )}
            >
              <option value="">{labels.preferredTimingPlaceholder}</option>
              {guidedRequestQuoteTimingOptions.map((option) => (
                <option key={option} value={option}>
                  {labels.timingOptions[option]}
                </option>
              ))}
            </select>
            {fieldErrors.preferredTiming ? (
              <span id={`${fieldId}-timing-error`} className="guided-request-quote__field-error">
                {fieldErrors.preferredTiming}
              </span>
            ) : null}
          </div>

          <label className="guided-request-quote__field">
            <span>{labels.timingNotesLabel}</span>
            <input
              type="text"
              value={answers.timingNotes}
              onChange={(event) => updateField("timingNotes", event.target.value)}
              placeholder={labels.timingNotesPlaceholder}
              maxLength={200}
              autoComplete="off"
            />
          </label>

          <div className="guided-request-quote__field">
            <label htmlFor={`${fieldId}-scope`}>{labels.scopeSummaryLabel}</label>
            <textarea
              id={`${fieldId}-scope`}
              rows={4}
              value={answers.scopeSummary}
              aria-invalid={Boolean(fieldErrors.scopeSummary)}
              aria-describedby={fieldErrors.scopeSummary ? `${fieldId}-scope-error` : undefined}
              onChange={(event) => updateField("scopeSummary", event.target.value)}
              placeholder={labels.scopeSummaryPlaceholder}
              maxLength={1200}
            />
            {fieldErrors.scopeSummary ? (
              <span id={`${fieldId}-scope-error`} className="guided-request-quote__field-error">
                {fieldErrors.scopeSummary}
              </span>
            ) : null}
          </div>

          <label className="guided-request-quote__field">
            <span>{labels.budgetRangeLabel}</span>
            <select
              value={answers.budgetRange}
              onChange={(event) => updateField(
                "budgetRange",
                event.target.value as GuidedRequestQuoteAnswers["budgetRange"],
              )}
            >
              <option value="">{labels.budgetRangePlaceholder}</option>
              {guidedRequestQuoteBudgetOptions.map((option) => (
                <option key={option} value={option}>
                  {labels.budgetOptions[option]}
                </option>
              ))}
            </select>
          </label>

          <label className="guided-request-quote__field">
            <span>{labels.additionalNotesLabel}</span>
            <textarea
              rows={3}
              value={answers.additionalNotes}
              onChange={(event) => updateField("additionalNotes", event.target.value)}
              placeholder={labels.additionalNotesPlaceholder}
              maxLength={600}
            />
          </label>

          {identitySlot}

          {fieldErrors.message ? (
            <p className="guided-request-quote__error" role="alert">{fieldErrors.message}</p>
          ) : null}

          <footer className="guided-request-quote__actions">
            <button type="button" onClick={onCancel} disabled={isSubmitting}>
              {labels.cancel}
            </button>
            <button
              type="button"
              className="guided-request-quote__primary"
              onClick={goToReview}
              disabled={isSubmitting}
            >
              {labels.reviewMessage}
            </button>
          </footer>
        </>
      ) : null}
    </section>
  );
}
